import IeqImage from "@/assets/igrejas/98x98/ieqcentralpiracicaba.webp";
import PoderosoSede from "@/assets/igrejas/98x98/igreja-pentecostal-poderoso-jesus-cristo.webp";
import QuadrangularOrlandaImage from "@/assets/igrejas/98x98/quadrangular-parque-orlanda.webp";
import QuadrangularTaquaral from "@/assets/igrejas/98x98/quadrangular-taquaral.webp";
import Zoe from "@/assets/igrejas/98x98/ministerio-cristao-zoe.webp";
import Maip from "@/assets/igrejas/98x98/maip.webp";
import ComunidadeCristaZelo from "@/assets/igrejas/98x98/comunidade-crista-zelo.webp";
import MinisterioDecisao from "@/assets/igrejas/98x98/ministerio-decisao.webp";
import { Link, useLocation } from "react-router-dom";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import { CONTAINER, scrollToSection } from "./ui-tokens";

// Esta lista é a fonte da contagem de clientes que o projeto declara em prosa.
// Hoje são oito, e o número aparece escrito em functions/emails/AGENTS.md e no
// comentário da prova social do hero.tsx — igreja que entra ou sai daqui muda
// os dois. Cada igreja daqui também precisa existir no mural "Igrejas que já
// usam" da apresentação comercial (scripts/apresentacao/apresentacao.tpl.html
// e o mapa de imagens do build.mjs), que é o mesmo argumento em PDF.
//
// O Cenáculo da Fé saiu daqui quando deixou de ser cliente; junto saíram o
// depoimento em destaque de testimonials.tsx, o case de /estatisticas e as
// duas aparições na apresentação. Os arquivos seguem em assets, sem import.
const churches = [
  { src: IeqImage, alt: "IEQ Central de Piracicaba", city: "Piracicaba, SP" },
  { src: QuadrangularTaquaral, alt: "IEQ Taquaral", city: "Piracicaba, SP" },
  {
    src: QuadrangularOrlandaImage,
    alt: "IEQ Parque Orlanda",
    city: "Piracicaba, SP",
  },
  { src: PoderosoSede, alt: "Poderoso de Jesus Cristo", city: "Brasil" },
  { src: MinisterioDecisao, alt: "Ministério Decisão", city: "Taquari, RS" },
  {
    src: Maip,
    alt: "Ministério Ap. Intimidade e Poder",
    city: "Guarulhos, SP",
  },
  {
    src: ComunidadeCristaZelo,
    alt: "Comunidade Cristã Zelo",
    city: "Duque de Caxias, RJ",
  },
  { src: Zoe, alt: "Ministério Cristão Zoé", city: "Águas Lindas de Goiás, GO" },
];

// Repetido para que cada metade do marquee (a que é deslocada em -50%) seja
// sempre mais larga que a viewport, mesmo em monitores ultra-wide — evita o
// "buraco" antes do loop reiniciar.
const marqueeChurches = [...churches, ...churches, ...churches, ...churches];

// Números agregados da plataforma inteira. Não confundir com os do case da
// IEQ Central (case-ieq.tsx), que são só daquela igreja: o 3.000 que estava
// aqui era o número dela, repetido como se fosse o total, e contradizia o
// "+4.400 pessoas" do hero na mesma rolagem. Pelo mesmo motivo o 3.000 não
// aparece mais em lugar nenhum da home.
//
// Os quatro são resultado que o comprador sente, não inventário de produto —
// "20 módulos" saiu daqui porque contagem de módulo é o que a gente acha
// interessante, não o que a igreja compra. O 72h é a mesma promessa de
// how-it-works.tsx e final-cta.tsx: se a operação deixar de sustentá-la,
// corrija nos três.
//
// O "+40%" é a exceção consciente: ele nasceu na IEQ Central e é lá que está
// atribuído, em /estatisticas. Fica aqui porque é o único dos quatro que fala
// do voluntário; quem quiser a atribuição a encontra no link logo abaixo. Se
// um dia ele sair, o substituto precisa ser sobre gente, não sobre o produto.
const numbers = [
  { value: "4.600+", label: "membros e visitantes gerenciados" },
  { value: "15 min", label: "para gerar as escalas do mês" },
  { value: "+40%", label: "de engajamento dos voluntários" },
  { value: "72h", label: "da planilha ao sistema operando" },
];

export default function TrustedBy() {
  const { trackNavClicked } = useDataFastGoals();
  // O case completo só existe na home (#case-ieq). Em outra página que use
  // esta faixa (/about), o mesmo link leva à página de estatísticas.
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  return (
    <section aria-label="Igrejas que usam o Ecclesiato" className="py-14 md:py-20">
      {/* ── Logos ──────────────────────────────────────────────────────── */}
      <p className={`${CONTAINER} mb-6 text-sm font-medium text-muted-foreground`}>
        Igrejas que gerem o dia a dia no Ecclesiato
      </p>

      {/* Leitor de tela lê a lista uma vez; a faixa que corre repete os nomes
          dezesseis vezes e fica só para os olhos. */}
      <ul className="sr-only">
        {churches.map((church) => (
          <li key={church.alt}>
            {church.alt}, {church.city}
          </li>
        ))}
      </ul>

      {/* Prova social, não vitrine de links: cada logo era um link para o
          Instagram da igreja — dezenas de portas de saída a uma tela do topo.
          `loading="lazy"`: abaixo da dobra, sem disputar banda com o hero. */}
      <div
        aria-hidden="true"
        className="relative overflow-hidden mask-x-from-90% mask-x-to-100%"
      >
        <div className="landing-marquee hover:paused flex w-max items-center">
          {[...marqueeChurches, ...marqueeChurches].map((church, idx) => (
            <div
              key={`${church.alt}-${idx}`}
              className="mx-6 flex shrink-0 items-center gap-3"
            >
              <img
                src={church.src}
                alt=""
                className="lp-img size-9 rounded-full object-cover grayscale"
                width={36}
                height={36}
                loading="lazy"
                decoding="async"
              />
              <span>
                <span className="block text-sm font-semibold whitespace-nowrap text-foreground">
                  {church.alt}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {church.city}
                </span>
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Números ────────────────────────────────────────────────────── */}
      <div className={`${CONTAINER} mt-14`}>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
          {numbers.map((n) => (
            <div key={n.label} className="flex flex-col-reverse gap-1">
              <dt className="text-sm leading-snug text-muted-foreground">
                {n.label}
              </dt>
              <dd className="text-4xl font-semibold tracking-tight text-foreground tabular-nums md:text-5xl">
                {n.value}
              </dd>
            </div>
          ))}
        </dl>

        <p className="mt-8 text-sm text-muted-foreground">
          Números medidos no uso real da plataforma.{" "}
          {onHome ? (
            <a
              href="#case-ieq"
              onClick={(e) => {
                e.preventDefault();
                trackNavClicked("trusted_by_to_case");
                scrollToSection("case-ieq");
              }}
              className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
            >
              Veja o case da IEQ Central de Piracicaba
            </a>
          ) : (
            <Link
              to="/estatisticas"
              onClick={() => trackNavClicked("trusted_by_to_case")}
              className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground"
            >
              Veja o case da IEQ Central de Piracicaba
            </Link>
          )}
        </p>
      </div>
    </section>
  );
}
